import axios from "../common";

export type DeviationCategory = {
  id: string;
  name: string;
  code: string;
  description: string;
  severity_level?: number;
  is_active?: boolean;
  created_at?: string;
};

function normalizeItem(raw: Record<string, unknown>): DeviationCategory {
  const attrs = (raw.attributes as Record<string, unknown> | undefined) ?? raw;
  return {
    id: String(raw.id ?? ""),
    name: String(attrs.name ?? ""),
    code: String(attrs.code ?? ""),
    description: String(attrs.description ?? ""),
    severity_level: (() => {
      const v = attrs.severity_level;
      if (v === undefined || v === null) {
        return undefined;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    })(),
    is_active: attrs.is_active as boolean | undefined,
    created_at: attrs.created_at as string | undefined,
  };
}

export function parseDeviationCategoryList(payload: unknown): DeviationCategory[] {
  if (payload == null) {
    return [];
  }
  if (Array.isArray(payload)) {
    return (payload as Record<string, unknown>[]).map((row) => normalizeItem(row));
  }
  const body = payload as { data?: unknown; results?: unknown[] };
  if (body?.data && Array.isArray(body.data)) {
    return (body.data as Record<string, unknown>[]).map((row) => normalizeItem(row));
  }
  if (body?.results && Array.isArray(body.results)) {
    return (body.results as Record<string, unknown>[]).map((row) => normalizeItem(row));
  }
  return [];
}

function parseOne(payload: unknown): DeviationCategory {
  const body = payload as { data?: Record<string, unknown> };
  if (body?.data) {
    return normalizeItem(body.data);
  }
  return normalizeItem(payload as Record<string, unknown>);
}

export type DeviationCategoryListPage = {
  results: DeviationCategory[];
  count: number;
  next: string | null;
  previous: string | null;
};

function parseListPage(payload: unknown): DeviationCategoryListPage {
  const body = payload as {
    results?: unknown;
    count?: number;
    next?: string | null;
    previous?: string | null;
  };
  if (body?.results != null && typeof body.count === "number") {
    return {
      results: parseDeviationCategoryList(body.results),
      count: body.count,
      next: body.next ?? null,
      previous: body.previous ?? null,
    };
  }
  const flat = parseDeviationCategoryList(payload);
  return {
    results: flat,
    count: flat.length,
    next: null,
    previous: null,
  };
}

export const apiListDeviationCategories = async (params?: {
  page?: number;
  page_size?: number;
}): Promise<DeviationCategoryListPage> => {
  const res = await axios.get("deviation-categories/", {
    withCredentials: true,
    params: {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 10,
    },
  });
  return parseListPage(res.data);
};

export const apiCreateDeviationCategory = async (body: {
  name: string;
  description?: string;
  severity_level?: number;
}): Promise<DeviationCategory> => {
  const res = await axios.post(
    "deviation-categories/",
    {
      name: body.name,
      description: body.description ?? "",
      ...(body.severity_level !== undefined
        ? { severity_level: body.severity_level }
        : {}),
    },
    {
      withCredentials: true,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    }
  );
  return parseOne(res.data);
};

export const apiPatchDeviationCategory = async (
  id: string,
  body: Partial<{
    name: string;
    description: string;
    code: string;
    severity_level: number;
    is_active: boolean;
  }>
): Promise<DeviationCategory> => {
  const res = await axios.patch(`deviation-categories/${id}/`, body, {
    withCredentials: true,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });
  return parseOne(res.data);
};

export const apiDeleteDeviationCategory = async (id: string): Promise<void> => {
  await axios.delete(`deviation-categories/${id}/`, { withCredentials: true });
};

/** POST: sustitución lógica origen → destino (spec simulation API). */
export const apiReplaceDeviationCategory = async (
  originId: string,
  destinyId: string
): Promise<DeviationCategory> => {
  const res = await axios.post(
    `deviation-categories/origin/${originId}/destiny/${destinyId}/`,
    {},
    {
      withCredentials: true,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    }
  );
  return parseOne(res.data);
};
